/**
 * Authentication Hook
 * Praat met de backend-login (/api/login) en bewaart een JWT in localStorage.
 * De return-API van de hook is behouden zodat aanroepers niet breken.
 */

// src/hooks/useAuth.js
import { useState, useEffect, useCallback } from 'react';
import { apiPost, setToken, clearToken, hasValidToken } from '../utils/apiClient';

export const useAuth = (authType = 'admin', requiredPassword = null) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(true);

  // Check existing authentication op basis van een geldige (niet-verlopen) JWT
  const checkAuthentication = useCallback(() => {
    setLoading(true);
    try {
      if (hasValidToken()) {
        setAuthenticated(true);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
    }

    setAuthenticated(false);
    setLoading(false);
  }, []);

  // Handle password submission via de backend
  const handlePasswordSubmit = useCallback(async () => {
    if (!password.trim()) {
      setAuthError('Password is required');
      return;
    }

    setLoading(true);
    try {
      // authType is 'admin' of 'schoorsteenveger' voor deze hook
      const res = await apiPost('/login', { type: authType, password });
      setToken(res.token);
      setAuthenticated(true);
      setAuthError('');
      setPassword('');
    } catch (error) {
      setAuthError('Invalid password');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
    // requiredPassword wordt niet meer client-side gevalideerd (server doet dit)
  }, [password, authType]);

  // Handle logout
  const logout = useCallback(() => {
    clearToken();
    setAuthenticated(false);
    setPassword('');
    setAuthError('');
  }, []);

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  // Check auth on mount
  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  return {
    authenticated,
    loading,
    password,
    setPassword,
    authError,
    handlePasswordSubmit,
    handleKeyPress,
    logout,
    authType
  };
};
